// src/lib/questions.ts
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { QuestionDoc } from '../types/questions';

/**
 * Fetch a single question by ID from Firestore
 */
export const getQuestion = async (id: string): Promise<QuestionDoc> => {
  try {
    const docRef = doc(db, 'questions', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Question with id ${id} not found`);
    }
    
    const data = docSnap.data() as Omit<QuestionDoc, 'id'>;
    return {
      id: docSnap.id,
      ...data,
      required: data.required ?? true, // default to true
      allowText: data.allowText ?? false,
    };
  } catch (err) {
    // FIX: Removed ': any' and cast to access .code safely
    const error = err as { code?: string };
    if (error.code === 'permission-denied') {
      console.warn(`getQuestion: Permissions missing for question ${id}.`);
    }
    throw err; 
  }
};

/**
 * Get the first question ID for the entire questionnaire
 * Looks for questions with isFirst: true
 */
export const getFirstQuestionId = async (): Promise<string> => {
  try {
    const questionsRef = collection(db, 'questions');
    const q = query(questionsRef, where('isFirst', '==', true));
    
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    
    console.warn('No question with isFirst:true found, using fallback');
    return 'FTU.GATE.0';
  } catch (err) {
    // FIX: Removed ': any' and cast
    const error = err as { code?: string };
    if (error.code === 'permission-denied') {
      console.warn('getFirstQuestionId: Permissions missing, using fallback FTU.GATE.0');
    } else {
      console.error('Error getting first question:', error);
    }
    return 'FTU.GATE.0';
  }
};

/**
 * Get journey start question from a gate option's 'next' field
 * @deprecated - routing should use option.next directly
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getGateStartId = (_optionValue: string): string | null => {
  console.warn('getGateStartId is deprecated - routing should use option.next');
  return null;
};

/**
 * Get journey configuration from Firestore config collection
 * Provides max steps and other metadata per journey
 */
export const getJourneyConfig = async (journeyPath: string) => {
  try {
    const configRef = doc(db, 'config', 'journeys');
    const configSnap = await getDoc(configRef);
    
    if (!configSnap.exists()) {
      console.warn('No journey config document found, using defaults');
      return getDefaultJourneyConfig(journeyPath);
    }
    
    const data = configSnap.data();
    return data[journeyPath] || getDefaultJourneyConfig(journeyPath);
  } catch (err) {
    // FIX: Removed ': any' and cast
    const error = err as { code?: string };
    if (error.code === 'permission-denied') {
      console.warn('getJourneyConfig: Permissions missing, using local defaults.');
    } else {
      console.error('Error getting journey config:', error);
    }
    return getDefaultJourneyConfig(journeyPath);
  }
};

/**
 * Fallback journey configuration if Firestore config doesn't exist
 */
const getDefaultJourneyConfig = (journeyPath: string) => {
  const defaults: Record<string, Record<string, unknown>> = {
    postnatal: {
      maxSteps: 9,
      displayName: 'Postnatal Recovery',
      enableRecoveryProgramme: true,
      recoveryProgrammeId: 'physical_wellbeing_6w',
      recoveryProgrammeWeeks: 6,
    },
    antenatal: {
      maxSteps: 4,
      displayName: 'Antenatal Preparation',
      enableRecoveryProgramme: false,
    },
    circumstances: {
      maxSteps: 2,
      displayName: 'Support Mode',
      enableRecoveryProgramme: false,
    },
  };
  
  return defaults[journeyPath] || {
    maxSteps: 5,
    displayName: 'Recovery Journey',
    enableRecoveryProgramme: false,
  };
};

/**
 * Check if a question ID represents a gate/routing question
 */
export const isGateQuestion = (id?: string | null): boolean => {
  if (!id) return false;
  return id.includes('.GATE.') || id.startsWith('SYS.');
};